import "bootstrap-icons/font/bootstrap-icons.css";
import "./ChipInput.scss";
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { copyTextToClipboard } from "../../utils";
import {
  calculateTagCount,
  TAGS_TOTAL_CHARACTER_LIMIT,
  TAGS_TOTAL_LENGTH_ERROR_MESSAGE,
} from "@/utils/mediautils/CommonFunctions";
import { toast } from "../Toast/Toast";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Object stored in the keywords array */
export interface TagKeyword {
  keyEnum: string;
  keywordName: string;
  relevance: string;
  source: string;
  metaInfo?: Record<string, unknown>;
}

/** Suggestion entry shown in the dropdown.
 *  All extra fields beyond keywordName/source are stored as-is when selected. */
export interface KeywordSuggestion extends TagKeyword {
  [key: string]: unknown;
}

export interface TagsChipInputProps {
  /** Field label */
  label?: string;
  /** Current chip values — array of keyword objects */
  value: TagKeyword[];
  /** Input placeholder */
  placeholder?: string;
  /** Called when chips are added, removed, or cleared */
  onChipChange: (
    chip: TagKeyword | TagKeyword[],
    action: "add" | "remove" | "clear"
  ) => void;
  /**
   * Called when the starred chip changes. Receives the full updated keywords
   * array with the new relevance values applied so the parent can set them
   * into formik in one shot.
   */
  onStarChange?: (updated: TagKeyword[]) => void;
  /** Max total character length across all chips (based on keywordName) */
  totalCharacterLimit?: number;
  /** Error message */
  error?: string;
  /** Whether the error state has been triggered */
  touched?: boolean;
  /** onBlur handler forwarded to the inner input */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Disables all interaction */
  disabled?: boolean;
  /** Shows the mandatory asterisk */
  showRequired?: boolean;
  /** Keyword suggestions fetched externally — when provided, a rich dropdown is shown */
  keywordSuggestions?: KeywordSuggestion[];
  /**
   * Called every time the internal input value changes (debounce should be
   * applied by the caller). Only fired when keywordSuggestions is provided.
   */
  onInputChange?: (text: string) => void;
  /** When true, shows a progress-bar loader at the bottom of the input box */
  isFetchingKeywords?: boolean;
  /**
   * When false, the keyword fetch progress bar is never shown (e.g. user has no
   * host/Denmark access). Defaults to true.
   */
  showKeywordFetchLoader?: boolean;
  /** When true, chips can only be added by selecting from the dropdown (no manual typed chips) */
  dropdownOnly?: boolean;
  /** When true, hides the star (mark as primary) affordance on chips */
  hideStar?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DROPDOWN_MAX_HEIGHT = 220;

/** Default fields (plus `keywordName`) for manually-typed / synced keywords */
export const MANUAL_TAG_KEYWORD_DEFAULTS = {
  keyEnum: "General",
  relevance: "20",
  source: "Orion",
} as const;

// ─── Component ────────────────────────────────────────────────────────────────

export const TagsChipInput: React.FC<TagsChipInputProps> = ({
  label,
  value,
  placeholder,
  onChipChange,
  onStarChange,
  totalCharacterLimit = TAGS_TOTAL_CHARACTER_LIMIT,
  error,
  touched,
  onBlur,
  disabled = false,
  showRequired = false,
  keywordSuggestions,
  onInputChange,
  isFetchingKeywords = false,
  showKeywordFetchLoader = true,
  dropdownOnly = false,
  hideStar = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isKeywordMode = Array.isArray(keywordSuggestions);

  // ── Derived values ──────────────────────────────────────────────────────────

  const chipNames = useMemo(
    () => (Array.isArray(value) ? value.map((kw) => kw.keywordName) : []),
    [value]
  );

  const committedChars = calculateTagCount(chipNames).tagCount;

  /** Keyword suggestions already filtered to exclude already-added chips */
  const filteredKeywords: KeywordSuggestion[] = isKeywordMode
    ? (keywordSuggestions ?? []).filter(
        (s) => !chipNames.includes(s.keywordName)
      )
    : [];

  const dropdownLength = filteredKeywords.length;
  const showDropdown =
    isFocused && isKeywordMode && inputValue.length > 0 && dropdownLength > 0;

  // Reset highlighted index whenever the dropdown list changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredKeywords.length]);

  // ── Chip logic ──────────────────────────────────────────────────────────────

  /**
   * Add a chip from a raw string (manual typing).
   * Produces a TagKeyword with the manual defaults.
   */
  const addManualChip = useCallback(
    (raw: string) => {
      const names = raw
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c.length > 0);

      let remaining = totalCharacterLimit - committedChars;
      const toAdd: TagKeyword[] = [];

      for (const name of names) {
        if (name.length <= remaining && !chipNames.includes(name)) {
          toAdd.push({
            ...MANUAL_TAG_KEYWORD_DEFAULTS,
            keywordName: name,
          });
          remaining -= name.length;
        }
      }

      if (toAdd.length > 0) {
        toAdd.forEach((kw) => onChipChange(kw, "add"));
        setInputValue("");
        onInputChange?.("");
      }
    },
    [
      committedChars,
      totalCharacterLimit,
      chipNames,
      onChipChange,
      onInputChange,
    ]
  );

  /**
   * Add a chip from a dropdown suggestion (carries the full object).
   */
  const addSuggestionChip = useCallback(
    (suggestion: KeywordSuggestion) => {
      if (chipNames.includes(suggestion.keywordName)) return;
      if (committedChars + suggestion.keywordName.length > totalCharacterLimit)
        return;
      // Store the full flat object from the API as-is
      onChipChange({ ...suggestion }, "add");
      setInputValue("");
      onInputChange?.("");
    },
    [
      chipNames,
      committedChars,
      totalCharacterLimit,
      onChipChange,
      onInputChange,
    ]
  );

  const removeChip = useCallback(
    (kw: TagKeyword) => {
      if (disabled) return;
      onChipChange(kw, "remove");
    },
    [disabled, onChipChange]
  );

  const clearAll = useCallback(() => {
    if (disabled) return;
    onChipChange([], "clear");
  }, [disabled, onChipChange]);

  // ── Input handlers ──────────────────────────────────────────────────────────

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    // When chips already exist, committing this input will cost an extra separator char (+1).
    if (committedChars + next.length > totalCharacterLimit) return;

    setInputValue(next);
    onInputChange?.(next);

    if (!dropdownOnly && next.endsWith(",")) {
      const chip = next.slice(0, -1).trim();
      if (chip) addManualChip(chip);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "Enter": {
        e.preventDefault();
        if (
          isKeywordMode &&
          highlightedIndex >= 0 &&
          highlightedIndex < dropdownLength
        ) {
          addSuggestionChip(filteredKeywords[highlightedIndex]);
          setHighlightedIndex(-1);
        } else if (!dropdownOnly && inputValue.trim()) {
          addManualChip(inputValue.trim());
        }
        break;
      }
      case "Backspace": {
        if (inputValue === "" && value.length > 0) {
          removeChip(value[value.length - 1]);
        }
        break;
      }
      case "ArrowDown": {
        e.preventDefault();
        if (isKeywordMode && dropdownLength > 0) {
          setHighlightedIndex((i) => (i < dropdownLength - 1 ? i + 1 : 0));
          scrollDropdownItem(highlightedIndex + 1);
        }
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        if (isKeywordMode && dropdownLength > 0) {
          setHighlightedIndex((i) => (i > 0 ? i - 1 : dropdownLength - 1));
          scrollDropdownItem(highlightedIndex - 1);
        }
        break;
      }
      case "Escape": {
        setHighlightedIndex(-1);
        break;
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (dropdownOnly) return;

    const text = e.clipboardData.getData("text/plain").trim();
    if (!text) return;

    const candidates = text
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0 && !chipNames.includes(c));

    let remaining = totalCharacterLimit - committedChars;
    const valid: TagKeyword[] = [];
    for (const name of candidates) {
      if (name.length <= remaining) {
        valid.push({ ...MANUAL_TAG_KEYWORD_DEFAULTS, keywordName: name });
        remaining -= name.length;
      } else {
        break;
      }
    }

    if (valid.length > 0) {
      onChipChange(valid, "add");
      setInputValue("");
      onInputChange?.("");
    }
  };

  // ── Dropdown scroll helper ──────────────────────────────────────────────────

  const scrollDropdownItem = (index: number) => {
    if (!dropdownRef.current) return;
    const item = dropdownRef.current.children[index] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  };

  // ── Border colour ───────────────────────────────────────────────────────────

  const borderColor = (() => {
    if (touched && error) return "1px solid #dc3545";
    if (isFocused) return "1px solid #86b7fe";
    return "1px solid var(--bg-input-border)";
  })();

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* ── Header row ── */}
      <div className="d-flex align-items-center gap-1 position-relative">
        <label className="form-label mb-0">{label}</label>
        {showRequired && <span className="required">*</span>}

        <small className="text-muted">
          ({committedChars}/{totalCharacterLimit})
        </small>

        {value.length > 0 && (
          <div
            className="d-flex align-items-center gap-2 position-absolute"
            style={{ right: "5px", top: "24px" }}
          >
            <i
              className="bi bi-copy fs-5"
              style={{ cursor: "pointer" }}
              title="Copy all tags"
              onClick={async () => {
                const ok = await copyTextToClipboard(chipNames.join(","));
                if (ok) {
                  toast.success("Tags copied to clipboard");
                } else {
                  toast.fail("Failed to copy tags to clipboard");
                }
              }}
            />
            {!disabled && (
              <i
                className="bi bi-x fs-3"
                style={{ cursor: "pointer" }}
                title="Clear all tags"
                onClick={clearAll}
              />
            )}
          </div>
        )}
      </div>

      {/* ── Input box ── */}
      <div
        className="position-relative"
        style={{
          border: borderColor,
          background: "#fff",
          borderRadius: "var(--bs-border-radius)",
        }}
      >
        {showKeywordFetchLoader &&
          isFetchingKeywords &&
          inputValue.length > 0 && (
            <>
              <div
                className="position-absolute bottom-0 start-0 w-100"
                style={{
                  height: "2px",
                  borderRadius:
                    "0 0 var(--bs-border-radius) var(--bs-border-radius)",
                  overflow: "hidden",
                }}
              >
                <div
                  key={inputValue}
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "#0d6efd",
                    animation: "progress 2s ease-in-out forwards",
                    transformOrigin: "left",
                    transform: "scaleX(0)",
                  }}
                />
              </div>
              <style>{`
              @keyframes progress {
                0% { transform: scaleX(0); }
                100% { transform: scaleX(1); }
              }
            `}</style>
            </>
          )}
        <div
          className="chip-input-wrapper form-control d-flex flex-wrap gap-1 row-gap-1 border-0 p-0"
          style={{ width: "90%" }}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            // keep focused if focus moved to dropdown
            if (dropdownRef.current?.contains(e.relatedTarget as Node)) return;
            setIsFocused(false);
            onBlur?.(e as React.FocusEvent<HTMLInputElement>);
          }}
        >
          {value.map((kw, i) => {
            const isStarred = kw.relevance === "100";
            const canToggleStar = !disabled && !!onStarChange && !hideStar;

            return (
              <span
                key={i}
                className="d-flex align-items-center px-2 rounded-3 mt-1"
                style={{
                  backgroundColor: "hsl(0,0%,90%)",
                  height: "22px",
                  alignSelf: "center",
                  color: "#333",
                  // fontWeight: 400,
                  // fontSize: "13px",
                  marginLeft: "0.375rem",
                  paddingLeft: canToggleStar ? undefined : "8px",
                  gap: "3px",
                  cursor: canToggleStar ? "pointer" : undefined,
                }}
                onClick={
                  canToggleStar
                    ? () => {
                        const updated = value.map((k) => ({
                          ...k,
                          relevance:
                            k.keywordName === kw.keywordName && !isStarred
                              ? "100"
                              : k.keywordName === kw.keywordName && isStarred
                                ? MANUAL_TAG_KEYWORD_DEFAULTS.relevance
                                : k.relevance === "100"
                                  ? MANUAL_TAG_KEYWORD_DEFAULTS.relevance
                                  : k.relevance,
                        }));
                        onStarChange(updated);
                      }
                    : undefined
                }
                title={
                  canToggleStar
                    ? isStarred
                      ? "Unstar"
                      : "Mark as primary (relevance 100)"
                    : undefined
                }
              >
                {!disabled && canToggleStar && (
                  <i
                    className={`bi ${isStarred ? "bi-star-fill" : "bi-star"} `}
                    aria-hidden
                    style={{
                      pointerEvents: "none",
                      color: isStarred ? "#f5a623" : "#3b82f6",
                      fontSize: "12px",
                    }}
                  />
                )}
                {kw.keywordName}
                {!disabled && (
                  <i
                    className="bi bi-x"
                    style={{ cursor: "pointer", paddingTop: "2px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeChip(kw);
                    }}
                  />
                )}
              </span>
            );
          })}

          <input
            ref={inputRef}
            type="text"
            className="chip-input"
            style={{ border: "none", outline: "none", minWidth: "100px" }}
            value={inputValue}
            placeholder={
              committedChars >= totalCharacterLimit ? undefined : placeholder
            }
            disabled={disabled}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={(e) => {
              if (dropdownRef.current?.contains(e.relatedTarget as Node))
                return;
              setIsFocused(false);
              onBlur?.(e);
            }}
          />
        </div>
      </div>

      {/* Inline hint only when Formik is not already showing TAGS_TOTAL_LENGTH_ERROR_MESSAGE */}
      {committedChars >= totalCharacterLimit &&
        !(touched && error === TAGS_TOTAL_LENGTH_ERROR_MESSAGE) && (
          <span className="text-danger small ms-1">
            {TAGS_TOTAL_LENGTH_ERROR_MESSAGE}
          </span>
        )}

      {/* ── Validation message ── */}
      {touched && error && (
        <div className="form-text text-danger px-2">{error}</div>
      )}

      {/* ── Keyword suggestion dropdown (match Bootstrap dropdown-menu / form selects) ── */}
      {showDropdown && (
        <div
          className="position-relative tags-chip-keyword-dropdown"
          style={{ zIndex: 1000 }}
        >
          <div
            className="dropdown-menu show position-absolute w-100 p-0 tags-chip-keyword-dropdown__menuShell"
            style={{ top: "2px" }}
          >
            <div
              ref={dropdownRef}
              className="tags-chip-keyword-dropdown__menuScroll"
              style={{ maxHeight: `${DROPDOWN_MAX_HEIGHT}px` }}
            >
              {filteredKeywords.map((kw, idx) => (
                <div
                  key={kw.keywordName}
                  role="option"
                  aria-selected={idx === highlightedIndex}
                  className={`dropdown-item tags-chip-keyword-dropdown__option d-flex align-items-center justify-content-between gap-2 ${
                    idx === highlightedIndex ? "is-highlighted" : ""
                  }`}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                  // mousedown fires before input blur — preventDefault keeps focus in the input
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addSuggestionChip(kw);
                    setHighlightedIndex(-1);
                    inputRef.current?.focus();
                  }}
                >
                  <span
                    className="me-1 min-w-0 flex-grow-1"
                    style={{
                      fontWeight: 400,
                      fontSize: "12px",
                      color: "var(--bs-body-color)",
                    }}
                  >
                    {kw.keywordName}
                  </span>
                  {kw.source && (
                    <span
                      className="tags-chip-keyword-dropdown__source flex-shrink-0"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {kw.source}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsChipInput;
